import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IconCode, ILanguageExtended } from "@multiweb/types/contracts";
import { DIContainer } from "Global/Base/DIContainer";
import { StringUtils } from "Global/Base/Utils/StringUtils";
import { ApplicationStorage, AppStorageItemType } from "OW.Remoting/Remoting/ApplicationStorage";
import { InvalidArgumentException } from "../../../../Global/Base/Exceptions";
import { EnumUtils } from "../../../../Global/Base/Utils/EnumUtils";
import { SupportedReportFormats } from "../../../../OW.Remoting/Remoting/ExportManager";
import { Action } from "../../../UI.Controls/Actions/Action";
import { ActionType, IAction, IMultiObjectAction } from "../../../UI.Controls/Actions/IAction";
import { DropDownMenu } from "../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ToolbarPages } from "../Toolbar/ToolbarPages";
import { MultiActionToolbarButton } from "../XGControls/Actions/MultiActionToolbarButton";
import { NodeAction } from "../XGControls/Actions/NodeAction";
import { ReportAction } from "../XGControls/Actions/ReportAction";
import { DesktopToolbarButton } from "../XGControls/Actions/ToolbarButton";
import { DesktopToolbarDropdownButton } from "../XGControls/Actions/ToolbarDropdownButton";
import { ToolbarMenuButton } from "../XGControls/Actions/ToolbarMenuButton";
import { DropDownMenuComboItem } from "../XGControls/Filters/DropDownMenuItems/DropDownMenuComboItem";
import { Form } from "../XGControls/Form";
import { FormMenu } from "../XGControls/FormMenu";
import { CalendarFrame } from "../XGControls/Frames/CalendarFrame";
import { ChartFrame } from "../XGControls/Frames/ChartFrame";
import { IBaseFrame } from "../XGControls/Frames/IBaseFrame";
import { IDetailFrame, IDetailMasterableFrame } from "../XGControls/Frames/IDetailMasterableFrame";
import { IForm } from "../XGControls/IForm";
import { IFrame } from "../XGControls/IFrame";
import { FormUICustomProperty, Icon, IFormGroupMenu, IFormMenu, IFormUI, IMenu, MenuTypes } from "./FormUI";
import { StandardActionDefinitionForUI } from "./FormUIShared";
import { MenuUIStandard, MenuUIStandardDetail } from "./MenuUIStandard";
import { MenuUIStandardCalendar } from "./MenuUIStandardCalendar";
import { MenuUIStandardChart } from "./MenuUIStandardChart";

const logger = LazyLoadingSectionLogger.create(LoggingSection.formUI);

export class FormUIStandard implements IFormUI {
    protected _formGroupMenu: IFormGroupMenu | undefined;
    protected _form: IForm;
    public refElement: HTMLElement;
    private _formMenu: FormMenuStandard | undefined;
    protected readonly _getLanguages: () => Promise<readonly ILanguageExtended[]>;

    public get formGroupMenu(): IFormGroupMenu | undefined {
        if (!this._formGroupMenu) {
            this._initFormGroupMenu();
        } return this._formGroupMenu;
    }
    public set formGroupMenu(v: IFormGroupMenu | undefined) {
        this._formGroupMenu = v;
    }
    public get formMenu(): IFormMenu | undefined {
        return this._formMenu;
    }
    public pages: ToolbarPages;

    //Je potřeba splnit implementaci interface. I když definici akcí potřebujeme mít přístupnou staticky.
    public get standardActionDefinitions(): Map<ActionType, StandardActionDefinitionForUI> {
        return FormUIStandard.standardActionDefinitions;
    }

    private _initFormGroupMenu(): void {
        //logger.log("InitFormGroupMenu");
        this._formGroupMenu = DIContainer.instance.create(IFormGroupMenu);
        //this._formGroupMenu.setProperty(FormUICustomProperty.breadcrumbsElement, DIContainer.instance.create(IBreadcrumbNav, undefined));
    }

    public static standardActionDefinitions = new Map<ActionType, StandardActionDefinitionForUI>([
        [ActionType.standardBack, { priority: -1, icon: Icon.back, locKey: "GUI-107" }], // Zpět

        // akce v toolbaru
        [ActionType.standardRefreshRecord, { priority: 0, icon: Icon.refresh, classes: ["no-caption"], locKey: "GUI-504088" }], //Obnovit
        [ActionType.standardPreviousRecord, { priority: 1, icon: Icon.prevRecord, classes: ["record-arrow", "no-caption"], locKey: "GUI-10" }], // Předchozí
        [ActionType.standardNextRecord, { priority: 2, icon: Icon.nextRecord, classes: ["record-arrow", "no-caption"], locKey: "GUI-11" }], // Následující
        [ActionType.standardRefreshList, { priority: 10, icon: Icon.refresh, classes: ["no-caption"], locKey: "GUI-504088" }], //Obnovit
        [ActionType.standardNewRecord, { priority: 11, icon: Icon.new, classes: ["no-caption"], locKey: "GUI-504082" }], //Přidat
        [ActionType.standardCopyRecord, { priority: 12, icon: Icon.copy, classes: ["no-caption"], locKey: "GUI-504076" }], // Kopírovat
        [ActionType.standardEditRecord, { priority: 13, icon: Icon.edit, classes: ["no-caption"], locKey: "GUI-504044" }], //Upravit
        [ActionType.standardDeleteRecord, { priority: 14, icon: Icon.delete, classes: ["no-caption"], locKey: "GUI-5" }], //Smazat
        [ActionType.standardSaveAll, { priority: 15, icon: Icon.save, locKey: "GUI-6" }], //Uložit
        [ActionType.standardSaveRecordInEditContext, { priority: 15, icon: Icon.none, locKey: "GUI-747264" }], // Hotovo
        [ActionType.standardCancelChanges, { priority: 16, icon: Icon.cancelChanges, locKey: "GUI-633052" }], //Zahodit změny
        [ActionType.standardDecrypt, { priority: 17, icon: Icon.userLockUnlocked, locKey: "GUI-631103" }], //Dešifrovat
        [ActionType.standardEditRecords, { priority: 18, icon: Icon.edit, locKey: "GUI-504044" }], // Upravit
        [ActionType.standardDeleteRecords, { priority: 19, icon: Icon.delete, locKey: "GUI-5" }], // Smazat
        [ActionType.standardMoveRightRecords, { priority: 24, icon: Icon.moveRight, locKey: "GUI-649337" }], // Přidat
        [ActionType.standardMoveLeftRecords, { priority: 25, icon: Icon.moveLeft, locKey: "GUI-649338" }], // Odebrat
        [ActionType.standardPrintAll, { priority: 26, icon: Icon.print, locKey: "GUI-659630" }], // Tisk seznamu
        [ActionType.standardPrintRecord, { priority: 27, icon: Icon.print, locKey: "GUI-659631" }], // Tisk záznamu
        [ActionType.standardExportCSV, { priority: 28, icon: Icon.export, locKey: "GUI-661080" }], // Export CSV
        [ActionType.standardExportExcel, { priority: 29, icon: Icon.export, locKey: "GUI-661081" }], // Export Excel
        [ActionType.standardDownloadAll, { priority: 30, icon: "doc-archive-download", locKey: "GUI-666840" }], // Stáhnout soubory
        // akce ve FrameMenu
        [ActionType.standardRecordsCount, { priority: 1, locKey: "GUI-282888" }], // Počet záznamů
        [ActionType.standardGraph, { priority: 2, icon: Icon.graph, locKey: "GUI-1500" }], // Graf
        [ActionType.standardColumnSettings, { priority: 3, icon: "ui-layout-third-vertical", locKey: "GUI-578321" }], // Nastavení sloupců
        [ActionType.standardToggleAggregationRow, { priority: 4, icon: IconCode.toolCompass }], // Text se mění dynamicky podle stavu patičky
        [ActionType.standardShowSubRecords, { priority: 5, icon: IconCode.true, locKey: "GUI-32", isTwoState: true }], // Zobrazit včetně podřízených

        // parametrický formulář
        [ActionType.standardOpenConditionForm, { priority: 1, icon: Icon.execute }],
        [ActionType.standardCloseAndOpenConditionForm, { priority: 2, icon: Icon.executeAndClose }],
        [ActionType.standardOpenResultForm, { priority: 3, icon: Icon.detailFormActionIcon }],
        [ActionType.standardRunMethod, { priority: 1, icon: Icon.execute }],
        [ActionType.standardRunMethodAndClose, { priority: 2, icon: Icon.executeAndClose }],
        [ActionType.standardCloseAfterWF, { priority: 1, icon: Icon.execute }],

        // MSLOV
        [ActionType.standardSelect, { priority: 19, icon: Icon.webTabs, locKey: "GUI-715443" }], // Označit
        [ActionType.standardSelectAll, { priority: 20, icon: Icon.selectAll, locKey: "GUI-303572" }], // Vybrat vše
        [ActionType.standardDeselectAll, { priority: 21, icon: Icon.selectNone, locKey: "GUI-669074" }], // Zrušit výběr
        [ActionType.standardShowAll, { priority: 22, icon: Icon.selectNone, locKey: "GUI-452722" }], //Zobrazit všechny
        [ActionType.standardShowFiltered, { priority: 23, icon: Icon.selectNone, locKey: "GUI-614590" }], // Skrýt záznamy neodpovídající filtru

        // kontextové akce
        [ActionType.standardSaveRecord, { priority: 1, icon: Icon.save, locKey: "GUI-6" }], //Uložit
        [ActionType.standardCancelRecordChanges, { priority: 2, icon: Icon.cancelChanges, classes: ["close", "flat", "icon", "no-caption"], locKey: "GUI-633052" }], //Zahodit změny
        [ActionType.standardOpenDetailOnMainForm, { priority: 4, icon: Icon.openOnMainForm, locKey: "GUI-672371" }], // Otevřít v novém okně
        [ActionType.standardAuditInfoRecord, { priority: 5, icon: Icon.glasses, locKey: "GUI-682155" }], // Auditní data
        [ActionType.standardShowWFHistory, { priority: 7, icon: Icon.wfHistory, locKey: "GUI-751958" }], // Zobrazit historii WF
        [ActionType.standardShowWFParticipants, { priority: 6, icon: Icon.wfParticipants, locKey: "GUI-702020" }], // Zobrazit hráče WF
        [ActionType.standardShowWFStatus, { priority: 8, icon: Icon.wfStatus, locKey: "GUI-725554" }], // Zobrazit stav WF
        [ActionType.standardInfoRecord, { priority: 9, icon: Icon.recordInfo, locKey: "GUI-663138" }], // Informace o záznamu

        // akce v řádku gridu
        [ActionType.standardSelectRecord, { priority: 1, icon: Icon.selectFromLov, locKey: "GUI-311684", classes: ["primary"] }], //Vybrat
        [ActionType.standardRowActions, { priority: 2, icon: Icon.recordActions, classes: ["actions", "flat", "icon", "no-caption"], locKey: "GUI-47796" }], //Akce
        [ActionType.standardOpenDetail, { priority: 3, icon: Icon.info, locKey: "GUI-580049", classes: ["flat", "icon", "no-caption"] }], //Detail záznamu

        // TLF
        [ActionType.standardMoveRightRecord, { priority: 0, icon: Icon.moveRight, locKey: "GUI-504082", classes: ["flat", "icon", "no-caption"] }], // Přidat
        [ActionType.standardMoveLeftRecord, { priority: 0, icon: Icon.moveLeft, locKey: "GUI-502207", classes: ["flat", "icon", "no-caption"] }], // Odebrat

        // akce z definice formuláře
        [ActionType.form, { priority: null, icon: Icon.detailFormActionIcon }],
        [ActionType.parametricForm, { priority: null, icon: Icon.defaultIcon }],
        [ActionType.insert, { priority: null, icon: Icon.defaultIcon, locKey: "GUI-504082" }], //Přidat
        [ActionType.method, { priority: null, icon: Icon.defaultIcon }],
        [ActionType.node, { priority: null, icon: Icon.defaultIcon }],
        [ActionType.workflow, { priority: null }],
        [ActionType.cryptoLabel, { priority: null, icon: "crypto-label" }],
        [ActionType.crypto, { priority: null }],
        [ActionType.report, { priority: null, icon: Icon.print }],
        [ActionType.reportAndClose, { priority: null, icon: Icon.print }],

        // akce kalendare
        [ActionType.standardPreviousInterval, { priority: 1, icon: Icon.prevRecord, classes: ["record-arrow", "calendar-toolbar-btn", "range", "prev", "no-caption"], locKey: "GUI-10" }],
        [ActionType.standardNextInterval, { priority: 3, icon: Icon.nextRecord, classes: ["record-arrow", "calendar-toolbar-btn", "range", "next", "no-caption"], locKey: "GUI-11" }],
        [ActionType.standardSetToday, { priority: 2, icon: Icon.defaultIcon, locKey: "GUI-247263", classes: ["calendar-toolbar-btn", "range", "today"] }],
        [ActionType.standardSwitchIntervalDay, { priority: 4, icon: Icon.defaultIcon, locKey: "GUI-301336", classes: ["calendar-toolbar-btn", "interval", "day"] }],
        [ActionType.standardSwitchIntervalWeek, { priority: 5, icon: Icon.defaultIcon, locKey: "GUI-301338", classes: ["calendar-toolbar-btn", "interval", "week"] }],
        [ActionType.standardSwitchIntervalMonth, { priority: 6, icon: Icon.defaultIcon, locKey: "GUI-301340", classes: ["calendar-toolbar-btn", "interval", "month"] }],
        //[ActionType.standardSwitchIntervalYear, { priority: 7, icon: Icon.defaultIcon, locKey: "GUI-301342", classes: ["calendar-toolbar-btn", "interval", "year"] }],

        // filtr
        [ActionType.standardFilter, { priority: 1, icon: Icon.filter, classes: ["no-caption"], locKey: "GUI-615970" }],
        [ActionType.standardFilterList, { priority: 2, icon: Icon.triangleBottom, locKey: "GUI-670216", classes: ["is-dropdown"] }], //Vše

        //fulltext search
        [ActionType.standardFulltextSearch, { priority: 1 }],

        //chart
        [ActionType.standardChartName, { priority: 0 }],
        [ActionType.standardMoreSettings, { priority: 1, icon: Icon.recordActions, classes: ["no-caption"], locKey: "GUI-674923" }], //Další možnosti
        [ActionType.standardChartSettings, { priority: 20, icon: Icon.settings, classes: ["no-caption"], locKey: "GUI-101" }], //Nastaveni
    ]);

    /**
     * Upraví vizuální vzhled akcí, které jsou definované ve FormUIStandard.standardActionDefinitions. Vrací false, pokud akce není definována.
     */
    public static customizeAction(action: IAction): boolean {
        const def = FormUIStandard.standardActionDefinitions.get(action.actionType);
        if (!def) {
            return false;
        }

        if (def.priority) {
            action.priority = def.priority;
        }

        if (!action.locKey && def.locKey) {
            action.locKey = def.locKey;
        }

        if (!action.icon && def.icon) {
            action.icon = def.icon;
        }

        return true;
    }

    /**
     * Vytváří tlačítko pro toolbar.
     * @param action Akce, pro kterou se má tlačítko vytvořit.
     * @param icon Ikona akce.
     * @param menuType Typ menu.
     * @param classes Další volné CSS třídy.
     * @param getLanguages Funkce umožňující načtení všech dostupných jazyků.
     */
    public static createToolbarButton(action: IAction, icon: Icon | string, menuType: MenuTypes, classes: string[], getLanguages: () => Promise<readonly ILanguageExtended[]>): void {
        if (action instanceof NodeAction)
            action.toolbarButton = new ToolbarMenuButton(icon, menuType, action, classes);
        else if (action instanceof ReportAction && action.showSettings) {
            const settings = FormUIStandard.createMenuReportSettings(action, Icon.settings, MenuTypes.contextMenu, undefined, getLanguages);
            const toolbarBtn = new DesktopToolbarDropdownButton(icon, menuType, action, classes);
            action.toolbarButton = toolbarBtn;
            toolbarBtn.dropDownMenu = settings;
        } else if (action instanceof IMultiObjectAction) {
            action.toolbarButton = new MultiActionToolbarButton(icon, menuType, action, classes);
        } else {
            action.toolbarButton = new DesktopToolbarButton(icon, menuType, action, classes);
        }
    }

    /**
     * Vytváří rozšířené pro tisk s volbou jazyka a výstupního formátu.
     * @param action Akce pro kterou se má menu vytvořit
     * @param icon Ikona akce.
     * @param menuType Typ menu.
     * @param _classes Další volné CSS třídy.
     * @param getLanguages Funkce umožňující načtení všech dostupných jazyků.
     */
    public static createMenuReportSettings(action: ReportAction, icon: Icon | string, menuType: MenuTypes, _classes: string[], getLanguages: () => Promise<readonly ILanguageExtended[]>): DropDownMenu {
        const btn = new DesktopToolbarDropdownButton(icon, menuType, action);
        action.toolbarButton = btn;
        btn.dropDownMenu.disposeOnDetach = false;
        btn.dropDownMenu.addItem(FormUIStandard._createMenuReportSettingsLang(action, getLanguages));
        btn.dropDownMenu.addItem(FormUIStandard._createMenuReportSettingsFormat(action));
        const element = new DropDownMenuItem();

        const printAction = new Action();
        printAction.enabled = true;
        printAction.locKey = "GUI-88358"; //Tisk

        printAction.action = async () => {
            action.print(action.selectedLangInCombo, action.selectedFormat);
        };

        element.action = printAction;
        element.icon = Icon[Icon.print];

        btn.dropDownMenu.addItem(element);
        return btn.dropDownMenu;
    }

    private static _createMenuReportSettingsFormat(reportAction: ReportAction): DropDownMenuComboItem {
        const list = EnumUtils.getEnumAsArray(SupportedReportFormats).map(item => {
            return { desc: item, code: item };
        });
        const defaultReportTypeIndex = this._getDefaultReportTypeIndex(list);

        const formatCombobox = new DropDownMenuComboItem();
        formatCombobox.disposeOnDetach = false;
        formatCombobox.required = true;
        const action = new Action();

        action.locKey = "GUI-603994"; //Formát

        formatCombobox.action = action;
        action.enabled = true;

        let defaultFormat = SupportedReportFormats[SupportedReportFormats.PDF];
        if (defaultReportTypeIndex >= 0) {
            defaultFormat = SupportedReportFormats[defaultReportTypeIndex];
        } else {
            logger.warn("Default reportType in initParams is not in supported reports formats");
        }

        formatCombobox.addItemsToCombobox(list);
        reportAction.selectedFormat = defaultFormat;
        formatCombobox.combobox.innerValue = defaultFormat;

        formatCombobox.combobox.valueChanged.add(() => {
            reportAction.selectedFormat = formatCombobox.combobox.innerValue;
        });

        return formatCombobox;
    }

    private static _createMenuReportSettingsLang(a: ReportAction, getLanguages: () => Promise<readonly ILanguageExtended[]>): DropDownMenuComboItem {
        const languageCombobox = new DropDownMenuComboItem();
        languageCombobox.disposeOnDetach = false;
        languageCombobox.required = true;

        const action = new Action();
        action.locKey = "GUI-602230";

        languageCombobox.action = action;
        action.enabled = true;
        getLanguages().then((languages) => {
            languageCombobox.addItemsToCombobox(languages.map(langInfo => {
                return { desc: langInfo.description, code: langInfo.code };
            }));
            const lang = ApplicationStorage.get(AppStorageItemType.reportLanguage);
            languageCombobox.combobox.innerValue = lang;
            a.selectedLangInCombo = lang; //cultures[0].Kod;
        });

        languageCombobox.combobox.valueChanged.add(() => {
            a.selectedLangInCombo = languageCombobox.combobox.innerValue;
        });

        return languageCombobox;
    }

    private static _getDefaultReportTypeIndex(objectArray: { desc: string; code: string}[]): number {
        const defaultReportType = ApplicationStorage.get(AppStorageItemType.reportType);
        return objectArray.findIndex(item => StringUtils.compareCaseInsensitive(item.desc, defaultReportType));
    }

    /**
     * Html reprezentace menu
     */
    public readonly element = document.createElement("div"); //samotne menu

    /**
     * @param form Formulář, ke kterému FormUI patří
     * @param getLanguages Funkce umožňující načtení všech dostupných jazyků.
     */
    constructor(form: IForm, getLanguages: () => Promise<readonly ILanguageExtended[]>) {
        this._form = form;
        this._getLanguages = getLanguages;

        this.refElement = form.element;
    }

    /**
     * Vytvoří formulářové menu s ovládacími prvky týkajícími se formuláře jako celku
     */
    public createFormMenu(): IFormMenu {
        if (!this._formMenu)
            this._formMenu = new FormMenuStandard(this.formGroupMenu, this._form);
        return this._formMenu;
    }

    /**
     * Vytvoří framové menu s ovládacími prvky týkajícími se jednoho konkrétního framu
     * @param sourceFrame Frame, ke kterému je menu vytvářeno nebo lokalizační klíč určující název framu
     */
    public createFrameMenu(sourceFrame: IFrame): IMenu {
        let newMenu: MenuUIStandard;

        if (sourceFrame instanceof IDetailFrame)
            newMenu = new MenuUIStandardDetail(this, sourceFrame.element, false, sourceFrame.frameName, sourceFrame, this._getLanguages);
        else if (sourceFrame instanceof IDetailMasterableFrame && sourceFrame.detail != null && sourceFrame.ownerForm.visibleOnlyDetail && !sourceFrame.visible)
            newMenu = new MenuUIStandardDetail(this, sourceFrame.element, false, sourceFrame.detail.frameName, sourceFrame, this._getLanguages); //seznam je skrytý, proto bude MasterNode pro akce nastaven DetailFrame
        else if (sourceFrame instanceof ChartFrame)
            newMenu = new MenuUIStandardChart(this, sourceFrame.element, false, sourceFrame.frameName, sourceFrame, this._getLanguages);
        else if (sourceFrame instanceof CalendarFrame) {
            newMenu = new MenuUIStandardCalendar(this, sourceFrame.container, false, sourceFrame.frameName, sourceFrame, this._getLanguages);
        } else {
            newMenu = this._createDefaultMenuUI(sourceFrame);
        }

        newMenu.createDefaultGroups();
        return newMenu;
    }

    // v ideálním případě tuto signaturu neměnit, používají ji vlastní implementace FormUI
    protected _createDefaultMenuUI(sourceFrame: IFrame): MenuUIStandard {
        return new MenuUIStandard(this, sourceFrame instanceof IBaseFrame ? sourceFrame.container : sourceFrame.element, false, sourceFrame.frameName, sourceFrame, this._getLanguages);
    }

    /**
     * Umožňuje nastavit vlastní specifické property, na které mohou různé implementace reagovat
     * @param propertyName Název property - buď enum FormUICustomProperty nebo vlastní libovolný string
     * @param newValue Nastavovaná hodnota
     */
    public setProperty(propertyName: FormUICustomProperty | string, newValue: any): void {
        if (propertyName == FormUICustomProperty.breadcrumbsElement) this._formMenu.setProperty(propertyName, newValue);
    }

    public dispose(): void {
        if (this._formMenu) { // Widgety nemají formMenu
            this._formMenu.dispose();
        }
    }

    /**
     * Jednoslovný název/kódové označení pro formUI.
     */
    public get formUIName(): string {
        return "standard";
    }
}

export class BtnCaption extends HTMLElement {
    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        this.classList.add("btn-caption");
    }

    protected static get observedAttributes(): string[] {
        return ["title"];
    }

    protected attributeChangedCallback(attrName: string, _oldVal: string, _newVal: string): void {
        if (attrName === "title") {
            this.dispatchEvent(new CustomEvent("titleChanged", {}));
        }
    }
}

export class FormGroupMenuStandard implements IFormGroupMenu {
    private _hidePageMenu?: boolean;

    public get hidePageMenu(): boolean {
        return this._hidePageMenu;
    }
    public set hidePageMenu(v: boolean) {
        this._hidePageMenu = v; this._element.style.display = !v ? "" : "none";
    }

    //private _hideMenuList: Array<HTMLElement>;

    protected _element: HTMLElement;
    private _formMenusContainer: HTMLElement;
    constructor() {
        this._constructor();
    }

    protected _constructor(): void {
        this._element = createElement({ classes: "form-group-menu-container" });

        document.getElementById("formsPlace").insertAdjacentElement("afterbegin", this._element);
    }

    private _addContainer<T extends HTMLElement>(container: T, target: "append" | "prepend" = "append"): T {
        if (target === "append") this._element.appendChild(container);
        else this._element.insertAdjacentElement("afterbegin", container);

        return container;
    }

    public setProperty(propertyName: string | FormUICustomProperty, newValue: any): void {
        if (propertyName == FormUICustomProperty.breadcrumbsElement) {
            if (newValue instanceof HTMLElement) {
                //const breadcrumbsElement = createElement({ classes: "breadcrumbs-menu-container"});
                //breadcrumbsElement.appendChild(newValue);
                //this._addContainer(breadcrumbsElement, "prepend");
                this._addContainer(newValue, "prepend");
            } else throw new InvalidArgumentException("[FormUI] Value for 'breadcrumbsElement' must be instance of HTMLElement");
        } else if (propertyName == FormUICustomProperty.formMenu) {
            if (newValue instanceof HTMLElement) {
                if (!this._formMenusContainer) {
                    this._formMenusContainer = createElement({ classes: "form-menu-container" });
                    this._element.insertAdjacentElement("beforeend", this._formMenusContainer);
                }
                this._formMenusContainer.appendChild(newValue);
            } else throw new InvalidArgumentException("[FormUI] Value for 'formMenu' must be instance of HTMLElement");
        }
    }

    public dispose(): void {
        // Prázdná metoda
    }
}
export class FormMenuStandard implements IFormMenu {
    //private readonly _masterFormUI: FormUIStandard;
    protected _formGroupMenu: IFormGroupMenu;
    private readonly _actions: IAction[] = [];
    //private readonly _element: HTMLElement;
    protected _formMenu: FormMenu;

    constructor(formsMenu: IFormGroupMenu, form: IForm) {
        this._createFormMenu(formsMenu, form);
    }

    protected _createFormMenu(formsMenu: IFormGroupMenu, form: IForm): void {
        this._formGroupMenu = formsMenu;
        if (!(form instanceof Form)) {
            logger.error("Unable to create FormMenu for form of different type than 'Form'");
            return;
        }
        this._formMenu = new FormMenu(form);
        this._formGroupMenu.setProperty(FormUICustomProperty.formMenu, this._formMenu.element);
    }

    public addAction<T extends IAction>(action: T): T {
        if (!FormUIStandard.customizeAction(action)) //akce není definována, nebude použita
            return null;

        if (action.actionType === ActionType.standardBack) {
            return null;
        }

        this._actions.push(action);
        return action;
    }

    public setProperty(propertyName: FormUICustomProperty | string, newValue: any): void {
        if (propertyName == FormUICustomProperty.formMenuVisibility && this._formMenu) {
            this._formMenu.visible = newValue;
        }
    }

    public dispose(): void {
        this._formMenu?.dispose();
    }
}

registerCustomElement("tsc-btncaption", BtnCaption);
